import { container } from 'tsyringe'
import { Color, LinearEncoding, MeshStandardMaterial, Texture, Vector2 } from 'three'
import { DebugFlag } from '@/common/constants'
import { Interface } from '@/shared/di/interface'
import type { IDebug } from '@/shared/di/interface/debug'
import type { IAssets } from '@/shared/di/interface/assets'

export default {
  getMaterial(normalMapId: string, diffuseMapId: string, denomination: number, gemColor = 0x000000) {
    const material = new MeshStandardMaterial({
      normalMap: new Texture(),
      map: new Texture(),
      metalness: 1,
      roughness: 0.05,
      envMapIntensity: 1,
      vertexColors: false,
      color: 0xffffff,
      normalScale: new Vector2(1, -1)
    })

    const assets = container.resolve<IAssets>(Interface.IAssets)

    material.normalMap = assets.getTexture(normalMapId)
    material.normalMap!.encoding = LinearEncoding
    material.normalScale = new Vector2(0.5, 0.5)
    material.map = assets.getTexture(diffuseMapId)
    material.userData.shader = {
      uniforms: {
        isBloomRender: { value: 0 },
        tintColor: { value: new Color(0xffffff) },
        crystalMultiplier: { value: denomination >= 70000 ? new Vector2(1.5, 1.5) : new Vector2(0.5, 0.5) },
        laserFactor: { value: 0 },
        normalRotation: { value: 0 }
      }
    }
    if (gemColor > 0) {
      material.defines['USE_CRYSTAL'] = ''
    }

    const debug = container.resolve<IDebug>(Interface.IDebug)
    if (material.defines['USE_CRYSTAL'] === '' && debug.getFlag(DebugFlag.FasterCoinMaterial)) {
      delete material.defines['USE_CRYSTAL']
    }

    material.onBeforeCompile = shader => {
      shader.uniforms.randomPerMesh = { value: Math.random() }
      shader.uniforms.gemColor = { value: new Color(gemColor) }
      shader.uniforms.normalRotation = material.userData.shader.uniforms.normalRotation
      shader.uniforms.tintColor = material.userData.shader.uniforms.tintColor
      shader.uniforms.laserFactor = material.userData.shader.uniforms.laserFactor
      shader.uniforms.crystalMultiplier = material.userData.shader.uniforms.crystalMultiplier
      if (gemColor > 0) {
        shader.uniforms.volumetricNoiseMap = {
          value: assets.getTexture(denomination >= 70000 ? 'coin-noise2' : 'coin-noise')
        }
      }

      shader.uniforms.isBloomRender = { value: 0 }

      shader.vertexShader = shader.vertexShader
        //add uniforms and textures
        .replace(
          '#include <common>',
          `
          #include <common>
          varying vec3 vWorldPosition;
          uniform float normalRotation;

          mat3 rotAxis(vec3 axis, float a) {
            float s=sin(a);
            float c=cos(a);
            float oc=1.0-c;
            vec3 as=axis*s;
            mat3 p=mat3(axis.x*axis,axis.y*axis,axis.z*axis);
            mat3 q=mat3(c,-as.z,as.y,as.z,c,-as.x,-as.y,as.x,c);
            return p*oc+q;
          }
        `
        )
        .replace(
          '#include <normal_vertex>',
          `
            //transformedNormal *= rotAxis(vec3(0.0,1.0,0.0), normalRotation);
            vNormal = normalize( transformedNormal );
        `
        )
        .replace(
          '#include <worldpos_vertex>',
          `
          vec4 worldPosition = modelMatrix * vec4(position,1.0);
          vWorldPosition = worldPosition.xyz;
        `
        )

      shader.fragmentShader = shader.fragmentShader
        //add uniforms and textures
        .replace(
          'uniform vec3 diffuse;',
          `
            uniform float randomPerMesh;
            uniform vec3 diffuse;
            uniform vec3 gemColor;
            uniform vec3 tintColor;
            uniform float laserFactor;
            uniform vec2 crystalMultiplier;
            uniform float isBloomRender;
            uniform sampler2D volumetricNoiseMap;
            varying vec3 vWorldPosition;
            uniform float normalRotation;

            mat3 rotAxis(vec3 axis, float a) {
              float s=sin(a);
              float c=cos(a);
              float oc=1.0-c;
              vec3 as=axis*s;
              mat3 p=mat3(axis.x*axis,axis.y*axis,axis.z*axis);
              mat3 q=mat3(c,-as.z,as.y,as.z,c,-as.x,-as.y,as.x,c);
              return p*oc+q;
            }
          `
        )
        .replace(
          '#include <envmap_physical_pars_fragment>',
          `
            vec3 getIBLIrradiance( const in vec3 normal ) {
              vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
              worldNormal *= rotAxis(vec3(0.0,1.0,0.0), normalRotation);
              vec4 envMapColor = textureCubeUV( envMap, worldNormal, 1.0 );
              return PI * envMapColor.rgb * envMapIntensity;
            }

            vec3 getIBLRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness ) {
              vec3 reflectVec = reflect( - viewDir, normal );
              reflectVec *= rotAxis(vec3(0.0,1.0,0.0), normalRotation);
              // Mixing the reflection with the normal is more accurate and keeps rough objects from gathering light from behind their tangent plane.
              reflectVec = normalize( mix( reflectVec, normal, roughness * roughness) );

              reflectVec = inverseTransformDirection( reflectVec, viewMatrix );

              vec4 envMapColor = textureCubeUV( envMap, reflectVec, roughness );

              return envMapColor.rgb * envMapIntensity;
            }
          `
        )
        .replace(
          '#include <color_fragment>',
          `
          float isSwatch5 = 1.0-smoothstep(0.0, 0.1,sampledDiffuseColor.r+sampledDiffuseColor.g+sampledDiffuseColor.b);

          float highlightMask = 0.0;
          vec4 noiseColor = vec4(0.0);

          #if defined( USE_CRYSTAL )
            vec3 normalInTangentSpace = vNormal;
            float random = 0.4 + 0.5*randomPerMesh + vWorldPosition.x*0.005 + vWorldPosition.y*0.005;
          
            // perlin-isch noise
            vec2 uvMultiplier = crystalMultiplier;
            float offsetAmount = texture2D( volumetricNoiseMap, vUv * 0.9 + vec2(random,random)).r*0.3;
            vec2 newUV = (vUv * uvMultiplier + vec2(random, random)) + vec2( (normalInTangentSpace.xy)) * offsetAmount;
            noiseColor = texture2D( volumetricNoiseMap, newUV)*2.0;
            
            highlightMask = smoothstep(0.1,0.6,(noiseColor.r + noiseColor.g + noiseColor.b)*0.3) * isSwatch5 * 2.0;
          #endif

          vec3 gemPlusNoiseColor = gemColor; //gemroom
          gemPlusNoiseColor *= noiseColor.rgb;
          
          diffuseColor.rgb = sampledDiffuseColor.rgb;
          diffuseColor.rgb += gemPlusNoiseColor * isSwatch5;
        `
        )
        //override metalnessFactor to disable it for crystal material
        .replace(
          '#include <metalnessmap_fragment>',
          `
          float metalnessFactor = metalness * (1.0-isSwatch5);
        `
        )
        //light calculation mix
        .replace(
          'vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;',
          `
          totalEmissiveRadiance = diffuseColor.rgb * ( 0.1 + 0.8 * highlightMask) * isSwatch5;
          vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;
        `
        )
        //final color, clamp and control bloom
        .replace(
          '#include <output_fragment>',

          `
          gl_FragColor = vec4( outgoingLight, 1.0 );
          
          gl_FragColor.rgb = mix(gl_FragColor.rgb, smoothstep(0.8,1.5,totalSpecular)*0.2+(smoothstep(0.8,1.0,abs(normal.x))) + totalEmissiveRadiance, isBloomRender);
          
          //add red
          gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * tintColor, laserFactor);
	        
          //save room machine light
          float machineLightAmount = (1.0-smoothstep(3.0, 5.6, vWorldPosition.y)) * max(0.0,(1.0-abs(vWorldPosition.x / 2.0)) * 0.5);
          machineLightAmount *= smoothstep(14.0, 15.0, vWorldPosition.z);
          gl_FragColor.rgb += vec3(0.0,0.6,0.6) * machineLightAmount;

          //gl_FragColor.rgb = clamp(gl_FragColor.rgb, vec3(0.0), vec3(1.4));

        `
        )

      material.userData.shader = shader
    }

    return material
  }
}
